import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { Picker } from '@arco-design/mobile-react';
import { IconArrowDown } from '@arco-design/mobile-react/esm/icon';

export type MobileSelectOption = { label: string; value: string };

type Props = {
  value: string;
  options: MobileSelectOption[];
  onChange: (value: string) => void;
  ariaLabel: string;
  placement?: 'top' | 'bottom';
  showDrawerTitle?: boolean;
  disabled?: boolean;
};

function MobileSelect(props: Props) {
  const [visible, setVisible] = useState(false);
  const [drawerTop, setDrawerTop] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const selectedLabel = props.options.find((option) => option.value === props.value)?.label ?? props.options[0]?.label ?? '';

  useEffect(() => {
    if (!visible || props.placement !== 'top') return;
    const updatePosition = () => {
      const filterPanel = triggerRef.current?.closest('.mobile-task-filter-panel');
      setDrawerTop(Math.max(0, filterPanel?.getBoundingClientRect().bottom ?? 0));
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setVisible(false);
    };
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [visible, props.placement]);

  const open = (event: MouseEvent<HTMLButtonElement>) => {
    if (props.placement === 'top') {
      const filterPanel = event.currentTarget.closest('.mobile-task-filter-panel');
      setDrawerTop(Math.max(0, filterPanel?.getBoundingClientRect().bottom ?? 0));
      setVisible((current) => !current);
      return;
    }
    setVisible(true);
  };

  return <>
    <button ref={triggerRef} className="mobile-flow-select" type="button" disabled={props.disabled} aria-label={props.ariaLabel} aria-haspopup={props.placement === 'top' ? 'listbox' : 'dialog'} aria-expanded={visible} onClick={open}>
      <span>{selectedLabel}</span><IconArrowDown aria-hidden="true" />
    </button>
    {props.placement === 'top' ? visible && createPortal(<div className="mobile-filter-layer" style={{ top: drawerTop }}>
      <button className="mobile-filter-layer__mask" type="button" aria-label="关闭筛选" onClick={() => setVisible(false)} />
      <div className="mobile-filter-drawer" style={{ maxHeight: `min(70dvh, 520px, calc(100dvh - ${drawerTop}px))` }}>
        {props.showDrawerTitle !== false ? <div className="mobile-filter-drawer__head">{props.ariaLabel}</div> : null}
        <div className="mobile-filter-drawer__options" role="listbox" aria-label={props.ariaLabel}>
          {props.options.map((option) => <button
            className={`mobile-filter-drawer__option${option.value === props.value ? ' is-selected' : ''}`}
            type="button"
            role="option"
            aria-selected={option.value === props.value}
            key={option.value}
            onClick={() => { props.onChange(option.value); setVisible(false); }}
          >{option.label}</button>)}
        </div>
      </div>
    </div>, document.body) : <Picker
      title={props.ariaLabel}
      data={props.options.map((option) => ({ label: option.label, value: option.value }))}
      value={[props.value]}
      visible={visible}
      onHide={() => setVisible(false)}
      onDismiss={() => setVisible(false)}
      onOk={(values) => {
        if (values[0] !== undefined) props.onChange(String(values[0]));
        setVisible(false);
      }}
    />}
  </>;
}

export default MobileSelect;
